/**
 * <pas-carousel> — horizontal carousel for the PAS product hero.
 *
 * Uses native CSS scroll-snap for smoothness, durability, and touch
 * support. To make the carousel feel infinite, real slides are cloned
 * once on each side:
 *   [HEAD clones (N) | REAL (N) | TAIL clones (N)]
 *
 * The carousel boots with scrollLeft pinned to the first real slide.
 * Clicking next/prev smooth-scrolls one step. When the scroll lands in
 * clone territory, scrollLeft is silently rewritten (no behavior) to the
 * equivalent real slide — the visible pixels are identical to the clone
 * we just landed on, so the jump is invisible to the user.
 */

// Movement (px) before a press counts as a gesture rather than a tap.
const DRAG_THRESHOLD = 8;
// Horizontal travel (px) that should always advance a slide, even when the
// pointer hasn't crossed the midpoint of the current one.
const FLICK_DISTANCE = 40;

class PasCarousel extends HTMLElement {
  connectedCallback() {
    // Idempotency: remove any clones left from a previous connect so re-running
    // connectedCallback (DOM re-insertion / theme-editor reorder) doesn't clone
    // the clones and multiply the slide count.
    this.querySelectorAll('[data-pas-clone]').forEach((c) => c.remove());

    // Fresh AbortController each connect so listeners from a prior connect are
    // already torn down (via disconnectedCallback) and this run's listeners are
    // tied to a live signal.
    this._ac?.abort();
    this._ac = new AbortController();
    const { signal } = this._ac;

    this.track = this.querySelector('[data-pas-track]');
    this.prev = this.querySelector('[data-pas-prev]');
    this.next = this.querySelector('[data-pas-next]');
    this.currentEl = this.querySelector('[data-pas-current]');

    const realSlides = Array.from(this.querySelectorAll('[data-pas-slide]'));
    if (!this.track || !realSlides.length) return;

    this.realCount = realSlides.length;
    this.realIndex = 0;
    this._busy = false;
    this._suppressSync = false;

    if (this.realCount <= 1) {
      if (this.prev) this.prev.disabled = true;
      if (this.next) this.next.disabled = true;
      this._renderCounter();
      return;
    }

    // Clone every real slide on each side for seamless wrap.
    const head = realSlides.map((s) => this._clone(s));
    const tail = realSlides.map((s) => this._clone(s));
    for (let i = head.length - 1; i >= 0; i--) {
      this.track.insertBefore(head[i], this.track.firstChild);
    }
    tail.forEach((c) => this.track.appendChild(c));

    this.allSlides = Array.from(this.querySelectorAll('[data-pas-slide]'));
    this.offset = this.realCount; // index of first real slide in allSlides

    if (this.prev) this.prev.disabled = false;
    if (this.next) this.next.disabled = false;

    this.prev.addEventListener('click', () => this.step(-1), { signal });
    this.next.addEventListener('click', () => this.step(1), { signal });

    // Manual swipe / trackpad scroll: keep the counter live while scrolling,
    // then once it settles, silently rewrap if we landed in clone territory.
    // This makes free-swiping infinite too — without it, swiping runs into the
    // cloned slides and dead-ends against the track edge (stuck near the last
    // slide). Debounced on `scroll` so it works in every browser (no reliance
    // on `scrollend`).
    this.track.addEventListener(
      'scroll',
      () => {
        if (this._busy || this._suppressSync) return;
        this._syncCounter();
        clearTimeout(this._settleTimer);
        this._settleTimer = setTimeout(() => this._normalizeAfterSwipe(), 140);
      },
      { passive: true, signal }
    );

    // Drag the image itself to move the carousel.
    // Touch already pans the scroll container natively (with momentum and
    // snap), so we only take the wheel for mouse/pen — where a native scroll
    // container responds to nothing. Both pointer types share the click guard,
    // so a swipe across a slide never opens the zoom lightbox.
    this.track.addEventListener('pointerdown', this._onPointerDown, { signal });

    // Slides are <img>, which the browser makes natively draggable. That native
    // drag steals the gesture and fires pointercancel a frame in, so without
    // this the carousel stops following the cursor almost immediately.
    this.track.addEventListener('dragstart', (event) => event.preventDefault(), { signal });

    // Pin scrollLeft to the first real slide once layout is settled.
    requestAnimationFrame(() => this._jumpToVisual(this.offset));
    this._renderCounter();
  }

  disconnectedCallback() {
    this._ac?.abort();
    this._dragAc?.abort();
    this._endDrag();
    this._restoreClicks();
    clearTimeout(this._settleTimer);
    clearTimeout(this._restoreTimer);
  }

  /* ---------------------------------------------------------------- drag -- */

  _onPointerDown = (event) => {
    // Secondary buttons, and mid-animation presses, are not drags.
    if (event.button > 0 || this._busy) return;

    this._dragAc?.abort();
    this._dragAc = new AbortController();
    const opts = { signal: this._dragAc.signal };

    this._dragStartX = event.clientX;
    this._dragStartY = event.clientY;
    this._dragMoved = false;
    this._dragging = false;
    // Mouse and pen get a synthetic drag; touch keeps native scrolling.
    this._pointerDrives = event.pointerType !== 'touch';

    document.addEventListener('pointermove', this._onPointerMove, opts);
    document.addEventListener('pointerup', this._onPointerUp, opts);
    // The browser fires pointercancel the moment it takes a touch over for
    // native scrolling — that is our signal that this was a swipe, not a tap.
    document.addEventListener('pointercancel', this._onPointerCancel, opts);
  };

  _onPointerMove = (event) => {
    const dx = event.clientX - this._dragStartX;
    const dy = event.clientY - this._dragStartY;

    if (!this._dragMoved) {
      if (Math.hypot(dx, dy) < DRAG_THRESHOLD) return;
      this._dragMoved = true;
      this._suppressClicks();
      // Only hijack a clearly horizontal intent — a vertical drag has to stay
      // available for scrolling the page.
      if (this._pointerDrives && Math.abs(dx) > Math.abs(dy)) this._startDrag();
    }

    if (!this._dragging) return;
    // Stops the browser turning the drag into a text/image selection.
    event.preventDefault();
    this.track.scrollLeft = this._dragStartScroll - dx;
  };

  _onPointerUp = (event) => {
    this._dragAc?.abort();
    const wasDragging = this._dragging;
    const dx = event.clientX - this._dragStartX;
    this._endDrag();

    if (wasDragging) {
      let target = this._closestVisual();
      if (Math.abs(dx) > FLICK_DISTANCE) {
        if (dx < 0 && target <= this._dragStartVisual) target = this._dragStartVisual + 1;
        if (dx > 0 && target >= this._dragStartVisual) target = this._dragStartVisual - 1;
      }
      this._settleTo(target);
    }

    if (this._dragMoved) this._scheduleClickRestore();
  };

  _onPointerCancel = () => {
    this._dragAc?.abort();
    const wasDragging = this._dragging;
    this._endDrag();
    // A cancelled synthetic drag leaves scrollLeft mid-slide; let snap settle it.
    if (wasDragging) this._settleTo(this._closestVisual());
    // Touch handed off to native scrolling: this was a swipe, so veto the tap.
    this._suppressClicks();
    // pointercancel means no pointerup is coming for this pointer, and the
    // click (if any) arrives at the *end* of the gesture — which for a long
    // swipe is well past any fixed timeout. Wait for the finger to lift.
    const done = () => {
      document.removeEventListener('touchend', done, true);
      document.removeEventListener('pointerup', done, true);
      this._scheduleClickRestore();
    };
    document.addEventListener('touchend', done, true);
    document.addEventListener('pointerup', done, true);
  };

  _startDrag() {
    this._dragging = true;
    this._dragStartScroll = this.track.scrollLeft;
    this._dragStartVisual = this._closestVisual();
    // Snap and smooth behaviour both fight a scrollLeft driven frame by frame.
    this.track.style.scrollSnapType = 'none';
    this.track.style.scrollBehavior = 'auto';
    this.track.style.userSelect = 'none';
    this.track.style.cursor = 'grabbing';
  }

  _endDrag() {
    if (!this._dragging) return;
    this._dragging = false;
    this.track.style.scrollSnapType = '';
    this.track.style.scrollBehavior = '';
    this.track.style.userSelect = '';
    this.track.style.cursor = '';
  }

  // Smooth-scroll to a slide after a drag, then rewrap out of clone territory
  // the same way `step()` does.
  _settleTo(visualIdx) {
    const target = Math.max(0, Math.min(this.allSlides.length - 1, visualIdx));
    this._busy = true;
    this._smoothScrollToVisual(target);
    this._afterScrollEnd(() => {
      this._busy = false;
      this._syncCounter();
      this._normalizeAfterSwipe();
    });
  }

  /* --------------------------------------------------------- click guard -- */

  // Horizon's delegated dispatcher (assets/component.js) resolves `on:click`
  // with `closest('[on\\:click]')` from a capture-phase listener on `document`,
  // which runs before anything we could attach here — so stopPropagation can't
  // veto it. Removing the attribute for the duration of the gesture can.
  _suppressClicks() {
    if (this._clicksSuppressed) return;
    this._clicksSuppressed = true;
    this.querySelectorAll('[on\\:click]').forEach((el) => {
      el.dataset.pasClickAttr = el.getAttribute('on:click');
      el.removeAttribute('on:click');
    });
  }

  _restoreClicks() {
    if (!this._clicksSuppressed) return;
    this._clicksSuppressed = false;
    this.querySelectorAll('[data-pas-click-attr]').forEach((el) => {
      el.setAttribute('on:click', el.dataset.pasClickAttr);
      delete el.dataset.pasClickAttr;
    });
  }

  // Restore once the click that follows this gesture has been swallowed. The
  // timeout covers gestures that never produce a click at all.
  _scheduleClickRestore() {
    const restore = () => {
      clearTimeout(this._restoreTimer);
      document.removeEventListener('click', restore, true);
      this._restoreClicks();
    };
    document.addEventListener('click', restore, true);
    this._restoreTimer = setTimeout(restore, 400);
  }

  _clone(slide) {
    const c = slide.cloneNode(true);
    c.setAttribute('aria-hidden', 'true');
    c.setAttribute('data-pas-clone', '');
    // Neutralize media players in clones. A cloned <video>/<iframe> would be a
    // second autoplaying player (extra decode, downloads, and the in-view
    // observer would drive it too). Clones exist only to make the wrap seamless,
    // and the real slide they mirror shows the live video — so swapping the
    // cloned player for its poster image is pixel-equivalent at rest. No-op for
    // image-only carousels (e.g. the lifestyle gallery), which have no media.
    const players = c.querySelectorAll('deferred-media, video, iframe, model-viewer');
    players.forEach((el) => {
      // Walk up to the deferred-media wrapper when present so we replace the
      // whole player, not just the inner node.
      const host = el.closest('deferred-media') || el;
      if (!host.parentNode || host.dataset.pasNeutralized) return;
      const posterSrc = this._posterFrom(host);
      const img = document.createElement('img');
      if (posterSrc.src) img.src = posterSrc.src;
      if (posterSrc.srcset) img.srcset = posterSrc.srcset;
      img.alt = posterSrc.alt;
      img.className = 'product-pas__hero-media';
      img.setAttribute('aria-hidden', 'true');
      img.dataset.pasNeutralized = '';
      host.replaceWith(img);
    });
    return c;
  }

  // Pull a usable poster image (src/srcset/alt) out of a cloned media host.
  _posterFrom(host) {
    const poster =
      host.querySelector('.deferred-media__poster-image') ||
      host.querySelector('img');
    if (!poster) return { src: '', srcset: '', alt: '' };
    return {
      src: poster.getAttribute('src') || '',
      srcset: poster.getAttribute('srcset') || '',
      alt: poster.getAttribute('alt') || '',
    };
  }

  step(direction) {
    if (this._busy) return;
    this._busy = true;

    const currentVisual = this._closestVisual();
    const targetVisual = currentVisual + direction;

    // Update real index + counter immediately so the UI feels responsive.
    this.realIndex = ((this.realIndex + direction) % this.realCount + this.realCount) % this.realCount;
    this._renderCounter();

    this._smoothScrollToVisual(targetVisual);

    this._afterScrollEnd(() => {
      // If we landed in clone territory, snap silently to the equivalent
      // real slide. The clone and the real slide look identical, so the
      // pixels on screen don't change — only scrollLeft moves.
      const visual = this._closestVisual();
      if (visual < this.offset || visual >= this.offset + this.realCount) {
        const wrapped = this.offset +
          (((visual - this.offset) % this.realCount) + this.realCount) % this.realCount;
        this._jumpToVisual(wrapped);
      }
      this._busy = false;
    });
  }

  _smoothScrollToVisual(visualIdx) {
    const slide = this.allSlides[visualIdx];
    if (!slide) return;
    this.track.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
  }

  _jumpToVisual(visualIdx) {
    const slide = this.allSlides[visualIdx];
    if (!slide) return;
    this._suppressSync = true;
    // Save/restore scroll-behavior so the CSS `smooth` rule doesn't animate
    // the silent jump.
    const prev = this.track.style.scrollBehavior;
    this.track.style.scrollBehavior = 'auto';
    this.track.scrollTo({ left: slide.offsetLeft, behavior: 'auto' });
    requestAnimationFrame(() => {
      this.track.style.scrollBehavior = prev;
      this._suppressSync = false;
    });
  }

  _closestVisual() {
    const scrollLeft = this.track.scrollLeft;
    let best = 0;
    let bestDist = Infinity;
    this.allSlides.forEach((slide, i) => {
      const dist = Math.abs(slide.offsetLeft - scrollLeft);
      if (dist < bestDist) {
        bestDist = dist;
        best = i;
      }
    });
    return best;
  }

  _syncCounter() {
    const visual = this._closestVisual();
    const real = (((visual - this.offset) % this.realCount) + this.realCount) % this.realCount;
    if (real !== this.realIndex) {
      this.realIndex = real;
      this._renderCounter();
    }
  }

  // After a manual swipe settles, if the nearest slide is a clone, silently
  // jump to the equivalent real slide. The clone and real slide are pixel-
  // identical, so nothing visibly moves — but it recentres the scroll position
  // inside the real band, keeping endless road in both directions.
  _normalizeAfterSwipe() {
    if (this._busy || this._suppressSync) return;
    const visual = this._closestVisual();
    if (visual < this.offset || visual >= this.offset + this.realCount) {
      const wrapped =
        this.offset +
        ((((visual - this.offset) % this.realCount) + this.realCount) % this.realCount);
      this._jumpToVisual(wrapped);
    }
  }

  _afterScrollEnd(cb) {
    let fired = false;
    const handler = () => {
      if (fired) return;
      fired = true;
      this.track.removeEventListener('scrollend', handler);
      clearTimeout(this._fallback);
      cb();
    };
    this.track.addEventListener('scrollend', handler);
    this._fallback = setTimeout(handler, 700);
  }

  _renderCounter() {
    if (this.currentEl) this.currentEl.textContent = String(this.realIndex + 1);
  }
}

if (!customElements.get('pas-carousel')) {
  customElements.define('pas-carousel', PasCarousel);
}
